import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";

export const FirstDeposit = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Making Your First Deposit</h1>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Learn how to deposit USDC to start trading on Nordex</p>
        </div>

        {/* Overview */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Overview</h2>
          <p className="text-muted-foreground mb-6">
            To trade on Nordex, you need to deposit USDC (USD Coin) into your trading account. 
            USDC serves as collateral for your positions and is used to calculate your profit and loss.
          </p>
          
          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <p className="text-sm">
              <strong>Note:</strong> Nordex supports deposits on Solana, Arbitrum, and Ethereum. 
              You can deposit USDC or USDT on any of these networks.
            </p>
          </div>
        </section>

        {/* Deposit Steps */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">How to Deposit</h2>
          
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                1
              </div>
              <div>
                <h3 className="font-semibold mb-1">Connect Your Wallet or Sign In</h3>
                <p className="text-sm text-muted-foreground">
                  Make sure you are connected to Nordex via your wallet or email/social login. 
                  You should see your account displayed in the top right corner of the screen.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                2
              </div>
              <div>
                <h3 className="font-semibold mb-1">Click the "Deposit" Button</h3>
                <p className="text-sm text-muted-foreground">
                  In the header, click the "Deposit" button to open the deposit dialog.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                3
              </div>
              <div>
                <h3 className="font-semibold mb-1">Select Asset and Network</h3>
                <p className="text-sm text-muted-foreground">
                  Choose your asset (USDC or USDT) and the blockchain network you want to deposit from:
                </p>
                <ul className="text-sm text-muted-foreground mt-2 space-y-1 pl-4 list-disc">
                  <li><strong className="text-foreground">Solana</strong> -- fast, typically arrives within 1 minute</li>
                  <li><strong className="text-foreground">Arbitrum</strong> -- medium speed, typically 3-5 minutes</li>
                  <li><strong className="text-foreground">Ethereum</strong> -- slower, typically 10-15 minutes</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                4
              </div>
              <div>
                <h3 className="font-semibold mb-1">Copy Your Deposit Address</h3>
                <p className="text-sm text-muted-foreground">
                  A unique deposit address for your selected network will be displayed. Copy the address 
                  using the copy button or scan the QR code with your mobile wallet.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                5
              </div>
              <div>
                <h3 className="font-semibold mb-1">Send Funds to the Address</h3>
                <p className="text-sm text-muted-foreground">
                  From your wallet or exchange, send the desired amount to the deposit address. 
                  Make sure to send the correct asset on the correct network.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                6
              </div>
              <div>
                <h3 className="font-semibold mb-1">Deposit Complete!</h3>
                <p className="text-sm text-muted-foreground">
                  Once the transaction is confirmed on-chain, your funds will appear in your trading account 
                  automatically. You'll see your balance updated in the account panel.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Getting USDC */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">How to Get USDC</h2>
          
          <p className="text-muted-foreground mb-4">
            If you don't already have USDC or USDT, here are some ways to get it:
          </p>

          <div className="space-y-3">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Buy from Centralized Exchanges</h3>
              <p className="text-sm text-muted-foreground">
                Purchase USDC or USDT from exchanges like Coinbase, Binance, or Kraken, then withdraw to your 
                wallet. Make sure to select the correct network (Solana, Arbitrum, or Ethereum) when withdrawing.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Swap on Decentralized Exchanges</h3>
              <p className="text-sm text-muted-foreground">
                Use DEXs like Jupiter (Solana), Uniswap (Ethereum/Arbitrum), or similar platforms 
                to swap other tokens for USDC directly in your wallet.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Bridge Between Chains</h3>
              <p className="text-sm text-muted-foreground">
                If you have USDC on a chain that isn't supported for deposit, you can use bridges like 
                Wormhole to transfer it to a supported network.
              </p>
            </div>
          </div>
        </section>

        {/* Important Notes */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Important Notes</h2>
          
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li><strong className="text-foreground">Correct network required</strong> -- only send the selected asset on the selected network to your deposit address. Sending the wrong asset or using the wrong network may result in permanent loss of funds.</li>
            <li><strong className="text-foreground">Transaction fees</strong> -- on-chain transactions require a small gas fee on the respective network. Solana fees are typically less than $0.01, while Ethereum fees vary with network congestion.</li>
            <li><strong className="text-foreground">Minimum deposit</strong> -- we recommend depositing at least 10 USDC or USDT to have sufficient collateral for opening positions.</li>
          </ul>
        </section>

        {/* Checking Your Balance */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Checking Your Balance</h2>
          
          <p className="text-muted-foreground mb-4">
            After depositing, you can view your balance in several places:
          </p>

          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Header Balance Display</strong> - Your total equity is shown next to the Deposit button
            </li>
            <li>
              <strong className="text-foreground">Balances Panel</strong> - The Balances panel (accessible via the "+" button) 
              shows a detailed breakdown of your Total Collateral, Available Balance, and Used Margin
            </li>
            <li>
              <strong className="text-foreground">Account Page</strong> - Navigate to the Account page for a comprehensive 
              view of your account status
            </li>
          </ul>
        </section>

        {/* Next Steps */}
        <section className="mb-12">
          <div className="p-6 border border-primary/20 rounded-lg bg-primary/5">
            <h3 className="text-lg font-semibold mb-2">Ready to Trade?</h3>
            <p className="text-sm text-muted-foreground">
              Now that you have USDC in your trading account, you're ready to place your first trade! 
              Continue to the next section to learn how.
            </p>
          </div>
        </section>

        <DocsNavigation 
          previous={{ title: "Connecting Your Wallet", href: "/connect-wallet" }}
          next={{ title: "Deposits & Withdrawals", href: "/withdrawals" }}
        />
      </div>
    </DocsLayout>
  );
};

